import { Component, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { AppLanguage, I18nService, TranslatePipe } from './i18n.service';
import { AppVersionService } from './app-version.service';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, TranslatePipe],
  template: `
    <div class="pb-24 lg:pb-8 min-h-screen lg:min-h-[calc(100vh-72px)] flex flex-col" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 sticky top-0 z-30 text-white shadow-sm flex items-center justify-between lg:hidden"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <div>
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ 'account' | t }}</p>
          <h1 class="text-lg font-semibold text-white/95 tracking-tight">{{ 'profile' | t }}</h1>
        </div>
        <button type="button" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15">
          <i class="ti ti-user-cog text-[20px]"></i>
        </button>
      </header>

      <main class="px-4 pt-5 flex-1 space-y-4 lg:px-7 lg:pt-6 lg:grid lg:grid-cols-[320px_minmax(0,1fr)] lg:gap-5 lg:items-start lg:space-y-0">
        <section class="bg-white rounded-2xl lg:rounded-lg p-5 border border-slate-100 text-center lg:sticky lg:top-[96px]">
          <img [src]="profilePic()" class="w-20 h-20 rounded-2xl mx-auto border border-slate-100 mb-4 object-cover" referrerpolicy="no-referrer" alt="Profile avatar">
          <h2 class="text-lg font-semibold text-slate-900">{{ userName() }}</h2>
          <p class="text-sm font-normal text-slate-500 mt-0.5">{{ userEmail() }}</p>

          <div class="flex items-center justify-center gap-2 mt-4">
            <span class="bg-emerald-50 text-emerald-700 border border-emerald-100 px-3 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider">
              {{ 'active' | t }}
            </span>
            <span class="bg-sky-50 text-sky-700 border border-sky-100 px-3 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider">
              {{ userRole() }}
            </span>
          </div>
        </section>

        <section class="grid grid-cols-3 gap-2.5 lg:col-start-2">
          <a routerLink="/work-list/jobs-active" class="bg-white rounded-2xl p-3 border border-slate-100 active:scale-[0.98] transition-all">
            <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{{ 'jobs' | t }}</p>
            <p class="text-2xl font-semibold text-slate-900">{{ jobStats().active }}</p>
            <p class="text-[10px] text-slate-400 mt-0.5">{{ 'active' | t }}</p>
          </a>
          <a routerLink="/work-list/projects-assigned" class="bg-white rounded-2xl p-3 border border-slate-100 active:scale-[0.98] transition-all">
            <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{{ 'projects' | t }}</p>
            <p class="text-2xl font-semibold text-slate-900">{{ projectCount() }}</p>
            <p class="text-[10px] text-slate-400 mt-0.5">{{ 'assigned' | t }}</p>
          </a>
          <div class="bg-white rounded-2xl p-3 border border-slate-100">
            <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">{{ 'shift' | t }}</p>
            <p class="text-2xl font-semibold text-slate-900">{{ shiftHours() }}</p>
            <p class="text-[10px] text-slate-400 mt-0.5">{{ 'hours' | t }}</p>
          </div>
        </section>

        @if (loadError()) {
          <section class="bg-rose-50 border border-rose-100 rounded-2xl p-4 flex items-start gap-3">
            <i class="ti ti-alert-circle text-rose-500 text-xl shrink-0"></i>
            <div class="flex-1">
              <p class="text-sm font-semibold text-rose-800">Some profile stats could not load</p>
              <p class="text-xs text-rose-600 mt-0.5">{{ loadError() }}</p>
            </div>
            <button (click)="loadSummary()" class="text-xs font-semibold text-rose-700 bg-white/70 border border-rose-100 rounded-lg px-3 py-1.5">
              {{ 'retry' | t }}
            </button>
          </section>
        }

        <section class="lg:col-start-2">
          <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">{{ 'workSummary' | t }}</p>
          <div class="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <a routerLink="/work-list/jobs-assigned" class="w-full p-4 flex items-center justify-between border-b border-slate-100 active:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center text-[#1A6FA6] border border-sky-100">
                  <i class="ti ti-briefcase text-xl"></i>
                </div>
                <div class="text-left">
                  <span class="font-semibold text-slate-800 text-sm block">{{ 'assignedJobs' | t }}</span>
                  <span class="text-xs font-normal text-slate-500">{{ jobStats().total }} total in your queue</span>
                </div>
              </div>
              <span class="text-sm font-semibold text-slate-600">{{ jobStats().total }}</span>
            </a>

            <a routerLink="/work-list/jobs-completed" class="w-full p-4 flex items-center justify-between border-b border-slate-100 active:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100">
                  <i class="ti ti-circle-check text-xl"></i>
                </div>
                <div class="text-left">
                  <span class="font-semibold text-slate-800 text-sm block">{{ 'completedWork' | t }}</span>
                  <span class="text-xs font-normal text-slate-500">Completed or ready for office</span>
                </div>
              </div>
              <span class="text-sm font-semibold text-slate-600">{{ jobStats().finished }}</span>
            </a>

            <a routerLink="/work-list/jobs-quotes" class="w-full p-4 flex items-center justify-between active:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 border border-amber-100">
                  <i class="ti ti-file-dollar text-xl"></i>
                </div>
                <div class="text-left">
                  <span class="font-semibold text-slate-800 text-sm block">{{ 'quoteFollowUp' | t }}</span>
                  <span class="text-xs font-normal text-slate-500">Quote sent or approved</span>
                </div>
              </div>
              <span class="text-sm font-semibold text-slate-600">{{ jobStats().quotes }}</span>
            </a>
          </div>
        </section>

        @if (canAccessAsBuilt()) {
          <section class="lg:col-start-2">
            <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">Field tools</p>
            <div class="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <a [routerLink]="asBuiltV2Enabled() ? '/as-built-v2' : '/as-built'" class="w-full p-4 flex items-center justify-between active:bg-slate-50 transition-colors">
                <div class="flex items-center gap-3 min-w-0">
                  <div class="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center text-[#1A6FA6] border border-sky-100 shrink-0">
                    <i class="ti ti-ruler-measure text-xl"></i>
                  </div>
                  <div class="text-left min-w-0">
                    <span class="font-semibold text-slate-800 text-sm block">{{asBuiltV2Enabled() ? 'As Built V2' : 'As Built'}}</span>
                    <span class="text-xs font-normal text-slate-500 block">Create and manage drainage drawings</span>
                  </div>
                </div>
                <i class="ti ti-chevron-right text-slate-300"></i>
              </a>
            </div>
          </section>
        }

        <section class="lg:col-start-2">
          <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">Customer reviews</p>
          <div class="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <a routerLink="/google-review-qr" class="w-full p-4 flex items-center justify-between active:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center text-[#1A6FA6] border border-sky-100 shrink-0">
                  <i class="ti ti-qrcode text-xl"></i>
                </div>
                <div class="text-left min-w-0">
                  <span class="font-semibold text-slate-800 text-sm block">Google Review QR</span>
                  <span class="text-xs font-normal text-slate-500 block">Open a QR code for the customer to scan</span>
                </div>
              </div>
              <i class="ti ti-chevron-right text-slate-300"></i>
            </a>
          </div>
        </section>

        <section class="lg:col-start-2">
          <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">{{ 'account' | t }}</p>
          <div class="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            <div class="w-full p-4 flex items-center justify-between border-b border-slate-100">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500 border border-slate-100">
                  <i class="ti ti-mail text-xl"></i>
                </div>
                <div class="text-left min-w-0">
                  <span class="font-semibold text-slate-800 text-sm block">{{ 'email' | t }}</span>
                  <span class="text-xs font-normal text-slate-500 truncate block max-w-[210px]">{{ userEmail() }}</span>
                </div>
              </div>
            </div>

            <div class="w-full p-4 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-500 border border-slate-100">
                  <i class="ti ti-id-badge text-xl"></i>
                </div>
                <div class="text-left">
                  <span class="font-semibold text-slate-800 text-sm block">{{ 'role' | t }}</span>
                  <span class="text-xs font-normal text-slate-500">{{ userRole() }}</span>
                </div>
              </div>
            </div>

            <button type="button" (click)="openPasswordModal()" class="w-full p-4 flex items-center justify-between border-t border-slate-100 active:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center text-[#1A6FA6] border border-sky-100">
                  <i class="ti ti-lock-password text-xl"></i>
                </div>
                <div class="text-left">
                  <span class="font-semibold text-slate-800 text-sm block">{{ 'changePassword' | t }}</span>
                  <span class="text-xs font-normal text-slate-500">Update your account password</span>
                </div>
              </div>
              <i class="ti ti-chevron-right text-slate-300"></i>
            </button>

            <a routerLink="/leave-requests" class="w-full p-4 flex items-center justify-between border-t border-slate-100 active:bg-slate-50 transition-colors">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600 border border-amber-100">
                  <i class="ti ti-calendar-plus text-xl"></i>
                </div>
                <div class="text-left">
                  <span class="font-semibold text-slate-800 text-sm block">Leave Requests</span>
                  <span class="text-xs font-normal text-slate-500">Request annual, sick, bereavement, parental, jury, or family violence leave</span>
                </div>
              </div>
              <i class="ti ti-chevron-right text-slate-300"></i>
            </a>

            <div class="w-full p-4 flex items-center justify-between border-t border-slate-100">
              <div class="flex items-center gap-3 min-w-0">
                <div class="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 border border-emerald-100">
                  <i class="ti ti-language text-xl"></i>
                </div>
                <div class="text-left min-w-0">
                  <span class="font-semibold text-slate-800 text-sm block">{{ 'language' | t }}</span>
                  <span class="text-xs font-normal text-slate-500 truncate block max-w-[170px]">{{ i18n.languageLabel() }}</span>
                </div>
              </div>
              <select [ngModel]="i18n.language()" (ngModelChange)="setLanguage($event)"
                      class="h-10 max-w-[132px] rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-700 outline-none focus:bg-white focus:border-[#1A6FA6]">
                @for (language of i18n.languages; track language.code) {
                  <option [ngValue]="language.code">{{ language.label }}</option>
                }
              </select>
            </div>

            <button type="button" (click)="version.clearCacheAndRefresh()" [disabled]="version.clearingCache()"
                    class="w-full p-4 flex items-center justify-between border-t border-slate-100 active:bg-slate-50 transition-colors disabled:opacity-60">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 border border-violet-100">
                  <i [class]="version.clearingCache() ? 'ti ti-loader-2 animate-spin text-xl' : 'ti ti-refresh-dot text-xl'"></i>
                </div>
                <div class="text-left">
                  <span class="font-semibold text-slate-800 text-sm block">Clear Cache & Refresh</span>
                  <span class="text-xs font-normal text-slate-500">Reload the latest mobile app version</span>
                </div>
              </div>
              <i class="ti ti-chevron-right text-slate-300"></i>
            </button>
          </div>
        </section>

        <button (click)="logout()" class="w-full h-12 bg-white rounded-xl lg:rounded-lg border border-rose-100 flex items-center justify-center gap-2 transition-all active:scale-[0.98] lg:col-start-2">
          <i class="ti ti-logout text-rose-500 text-xl"></i>
          <span class="font-semibold text-rose-600 text-sm">{{ 'logOut' | t }}</span>
        </button>
      </main>

      @if (showPasswordModal()) {
        <div class="fixed inset-0 z-[80] flex items-center justify-center p-4" style="background:rgba(15,23,42,0.45);">
          <form (ngSubmit)="submitPasswordChange()" class="bg-white rounded-2xl border border-slate-100 p-5 w-full max-w-[380px]">
            <div class="w-12 h-12 rounded-2xl mx-auto mb-4 bg-sky-50 text-[#1A6FA6] flex items-center justify-center">
              <i class="ti ti-lock-password text-2xl"></i>
            </div>
            <h2 class="text-lg font-semibold text-slate-900 text-center">Change Password</h2>
            <p class="text-sm text-slate-500 text-center mt-2">Enter your current password and choose a new one.</p>

            @if (passwordError()) {
              <div class="mt-4 bg-rose-50 border border-rose-100 rounded-xl px-3 py-2 text-sm text-rose-700">
                {{ passwordError() }}
              </div>
            }

            <div class="space-y-3 mt-4">
              <label class="block">
                <span class="text-xs font-medium text-slate-500 mb-1 block">Current Password</span>
                <div class="relative">
                  <input [type]="showCurrentPassword() ? 'text' : 'password'" [(ngModel)]="passwordForm.current" name="currentPassword"
                         class="w-full h-11 px-3 pr-11 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                  <button type="button" (click)="showCurrentPassword.update(v => !v)" class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg text-slate-400">
                    <i [class]="showCurrentPassword() ? 'ti ti-eye-off' : 'ti ti-eye'"></i>
                  </button>
                </div>
              </label>

              <label class="block">
                <span class="text-xs font-medium text-slate-500 mb-1 block">New Password</span>
                <div class="relative">
                  <input [type]="showNewPassword() ? 'text' : 'password'" [(ngModel)]="passwordForm.next" name="newPassword"
                         class="w-full h-11 px-3 pr-11 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
                  <button type="button" (click)="showNewPassword.update(v => !v)" class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-lg text-slate-400">
                    <i [class]="showNewPassword() ? 'ti ti-eye-off' : 'ti ti-eye'"></i>
                  </button>
                </div>
              </label>

              <label class="block">
                <span class="text-xs font-medium text-slate-500 mb-1 block">Confirm New Password</span>
                <input [type]="showNewPassword() ? 'text' : 'password'" [(ngModel)]="passwordForm.confirm" name="confirmPassword"
                       class="w-full h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1A6FA6] outline-none text-sm">
              </label>
            </div>

            <div class="grid grid-cols-2 gap-3 mt-5">
              <button type="button" (click)="closePasswordModal()" [disabled]="isChangingPassword()"
                      class="h-11 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 font-semibold text-sm disabled:opacity-60">
                Cancel
              </button>
              <button type="submit" [disabled]="isChangingPassword()"
                      class="h-11 rounded-xl bg-[#102C41] text-white font-semibold text-sm disabled:opacity-60">
                @if (isChangingPassword()) {
                  <i class="ti ti-loader animate-spin"></i>
                } @else {
                  Update
                }
              </button>
            </div>
          </form>
        </div>
      }
    </div>
  `
})
export class ProfileComponent {
  api = inject(ApiService);
  i18n = inject(I18nService);
  version = inject(AppVersionService);
  router = inject(Router);

  jobs = signal<any[]>([]);
  projects = signal<any[]>([]);
  shiftSeconds = signal(0);
  loadError = signal('');
  showPasswordModal = signal(false);
  isChangingPassword = signal(false);
  passwordError = signal('');
  showCurrentPassword = signal(false);
  showNewPassword = signal(false);
  passwordForm = {
    current: '',
    next: '',
    confirm: '',
  };

  currentUser = computed(() => this.api.currentUser() || {});
  userName = computed(() => this.currentUser().name || 'Plumber');
  userEmail = computed(() => this.currentUser().email || 'No email available');
  userRole = computed(() => this.currentUser().role || 'Worker');
  canAccessAsBuilt = computed(() => String(this.currentUser().role || '') === 'Drainlayer');
  profilePic = computed(() => this.currentUser().profile_photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(this.userName())}&background=0F334A&color=fff`);

  jobStats = computed(() => {
    const jobs = this.jobs();
    return {
      total: jobs.length,
      active: jobs.filter(j => j.status === 'In Progress').length,
      finished: jobs.filter(j => ['Ready for Office', 'Completed'].includes(j.status)).length,
      quotes: jobs.filter(j => ['Quote Sent', 'Approved'].includes(j.status)).length,
    };
  });
  projectCount = computed(() => this.projects().filter(p => p.status !== 'Completed').length);
  shiftHours = computed(() => (this.shiftSeconds() / 3600).toFixed(1));

  setLanguage(language: AppLanguage) {
    this.i18n.setLanguage(language);
  }

  constructor() {
    this.loadSummary();
    inject(HttpClient).get<any>(`${this.api.baseUrl}/as_built_v2/index.php?action=settings`, {
      headers: new HttpHeaders({ Authorization: `Bearer ${this.api.accessToken()}` })
    }).subscribe({ next: res => this.asBuiltV2Enabled.set(!!res?.data?.staff_enabled), error: () => {} });
  }

  asBuiltV2Enabled = signal(false);

  loadSummary() {
    this.loadError.set('');
    this.api.getJobs().subscribe({
      next: (res: any) => {
        if (res.success) this.jobs.set(res.data?.jobs || []);
        else this.loadError.set(res.message || 'Jobs could not be loaded.');
      },
      error: (err: any) => this.loadError.set(err?.error?.message || 'Jobs could not be loaded.')
    });

    this.api.getProjects().subscribe({
      next: (res: any) => {
        if (res.success) this.projects.set(res.data?.projects || []);
      },
      error: () => this.loadError.set('Projects could not be loaded.')
    });

    this.api.getShiftStatus().subscribe({
      next: (res: any) => {
        if (res.success) this.shiftSeconds.set(Number(res.data?.today_seconds || 0));
      },
      error: () => this.loadError.set('Shift hours could not be loaded.')
    });
  }

  logout() {
    this.api.logout();
    this.router.navigate(['/login']);
  }

  openPasswordModal() {
    this.passwordError.set('');
    this.passwordForm = { current: '', next: '', confirm: '' };
    this.showCurrentPassword.set(false);
    this.showNewPassword.set(false);
    this.showPasswordModal.set(true);
  }

  closePasswordModal() {
    if (this.isChangingPassword()) return;
    this.showPasswordModal.set(false);
    this.passwordError.set('');
  }

  submitPasswordChange() {
    this.passwordError.set('');
    const current = this.passwordForm.current.trim();
    const next = this.passwordForm.next.trim();
    const confirm = this.passwordForm.confirm.trim();

    if (!current || !next || !confirm) {
      this.passwordError.set('Please fill in all password fields.');
      return;
    }
    if (next.length < 6) {
      this.passwordError.set('New password must be at least 6 characters long.');
      return;
    }
    if (next !== confirm) {
      this.passwordError.set('New password and confirmation do not match.');
      return;
    }
    if (current === next) {
      this.passwordError.set('New password must be different from your current password.');
      return;
    }

    this.isChangingPassword.set(true);
    this.api.changePassword({ current_password: current, new_password: next }).subscribe({
      next: (res: any) => {
        this.isChangingPassword.set(false);
        if (res.success) {
          this.showPasswordModal.set(false);
          showToast('success', 'Password changed successfully.');
        } else {
          this.passwordError.set(res.message || 'Password could not be changed.');
        }
      },
      error: (err: any) => {
        this.isChangingPassword.set(false);
        this.passwordError.set(err?.error?.message || 'Password could not be changed.');
      }
    });
  }
}
